import React from 'react';
import Link from 'next/link';
import SEOHeading from '@/components/common/SEOHeading';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  rating: number;
  text: string;
  puppyName: string;
  image: string;
  alt: string;
  date: string;
}

const mockTestimonials: Testimonial[] = [
{
  id: 1,
  name: "Sarah Johnson",
  location: "Philadelphia, PA",
  rating: 5,
  text: "We couldn't be happier with our yellow lab, Bella! The breeder was incredibly professional, knowledgeable, and genuinely cared about finding the perfect match for our family. Bella is healthy, beautifully socialized, and has the sweetest temperament imaginable.",
  puppyName: "Bella",
  image: "https://images.unsplash.com/photo-1627907553439-3f600cbcd262",
  alt: "Happy woman with blonde hair smiling while holding yellow Labrador puppy outdoors",
  date: "December 2025"
},
{
  id: 2,
  name: "Michael Chen",
  location: "New York, NY",
  rating: 5,
  text: "Outstanding experience from beginning to end. The health guarantee gave us complete peace of mind, and the ongoing support has been truly invaluable. Our chocolate lab Max is everything we hoped for and so much more!",
  puppyName: "Max",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6ab1acb-1763294205230.png",
  alt: "Asian man in casual shirt holding chocolate Labrador puppy with warm smile",
  date: "November 2025"
},
{
  id: 3,
  name: "Emily Rodriguez",
  location: "Baltimore, MD",
  rating: 5,
  text: "After researching many different breeders, we chose LuminexLabradors and it was absolutely the best decision. Our black lab Cooper is smart, energetic, and has become an integral part of our family. We highly recommend them!",
  puppyName: "Cooper",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ce81ee4-1763294834550.png",
  alt: "Hispanic woman with dark hair cuddling black Labrador puppy in home setting",
  date: "January 2026"
}];


const TestimonialsPreview = () => {
  return (
    <section className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 py-16 lg:py-24">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="mb-12 text-center">
          <SEOHeading level={2} className="mb-4">
            Hear From Our Happy Families
          </SEOHeading>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Don't just take our word for it. Read what families across the country are saying about their experience with Luminex Labradors.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {mockTestimonials.map((testimonial) =>
          <div
            key={testimonial.id}
            className="flex flex-col rounded-xl bg-card p-6 shadow-warm transition-smooth hover:shadow-warm-md">

              {/* Rating */}
              <div className="mb-4 flex items-center gap-1">
                {[...Array(testimonial.rating)].map((_, i) =>
              <svg
                key={i}
                className="h-5 w-5 text-warning"
                fill="currentColor"
                viewBox="0 0 20 20">

                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
              )}
              </div>

              {/* Testimonial Text */}
              <p className="mb-6 flex-grow text-muted-foreground">
                "{testimonial.text}"
              </p>

              {/* Author Info */}
              <div className="flex items-center gap-3 border-t border-border pt-4">
                <div className="relative h-12 w-12 overflow-hidden rounded-full">
                  <AppImage
                  src={testimonial.image}
                  alt={testimonial.alt}
                  className="h-full w-full object-cover" />

                </div>
                <div className="flex-grow">
                  <p className="font-semibold text-foreground">
                    {testimonial.name}
                  </p>
                  <p className="caption text-muted-foreground">
                    {testimonial.location} • {testimonial.puppyName}'s Family
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View All Button */}
        <div className="mt-12 text-center">
          <Link
            href="/customer-testimonials"
            className="inline-flex h-14 items-center justify-center gap-2 rounded-md border-2 border-primary bg-transparent px-8 font-semibold text-primary transition-smooth hover:bg-primary hover:text-primary-foreground focus-ring">

            Read More Testimonials
            <Icon name="ArrowRightIcon" size={20} variant="outline" />
          </Link>
        </div>
      </div>
    </section>);

};

export default TestimonialsPreview;